import React, { useContext, useMemo, useState } from 'react';

import { Popover } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Transforms } from 'slate';
import { useSlate } from 'slate-react';

import ComponentTypesIconWrapper from '../../../components/componentTypesIcon/ComponentTypesIconWrapper';
// components
import ConfirmPrompt from '../../../components/modals/confirmPrompt';
import AampeToggleButton, {
    ToggleButtonProps,
} from '../../../components/shared/AampeToggleButton/AampeToggleButton';
import useComponentTypeUpdate from '../../../hooks/components/useComponentTypeUpdate';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../constants/aampeTheme';
import { FormulaType } from '../../../constants/formulaType';
import ICONS from '../../../constants/icons';

// context
import { EditorContext } from '../../../context/EditorContext';

import VariantTypeDropdown from '../../../editors/PushNotificationEditor/Sidebar/VariantTypeDropdown';

import confirm from '../../../helpers/confirm';

import alternateKeys from '../../../hooks/alternates/_queryKeys';
import useComponentTypesQuery from '../../../hooks/component-types/useComponentTypesQuery';
import useAnalytics from '../../../hooks/helper/useAnalytics';

import { ComponentTypeMapped } from '../../../models/component-types-model';
// models
import { ElementKind } from '../../../models/editor.model';
// constant
import { FormulaTypesData } from '../../../models/formula.model';

import MessagePreviewMain from '../messagePreview/MessagePreviewMain';
import ContentTag from './ContentTag';
import { ToggleButtonValues } from './FormulaEditorMain';
import AssistantSteps, {
    AssistantStepsProps,
} from './assistant/AssistantSteps';
import WhatsAppTemplateMain from './whatsapp-template/WhatsAppTemplateMain';

interface SidebarMainProps extends AssistantStepsProps {
    isVariationTab: ToggleButtonValues;
    variantId?: string;
    variantType?: string;
    editVariantType: (
        id: string,
        variantType: string,
        componentId: string,
    ) => void;
    hideToggleButtons?: boolean;
    hasWriteAccess: boolean;
}

const SidebarMain: React.FC<React.PropsWithChildren<SidebarMainProps>> = ({
    isVariationTab,
    setIsVariationTab,
    formulaId,
    selectedElement,
    setSelectedElement,
    alternateCreateError,
    setAlternateCreateError,
    variantId,
    currentSelection,
    editVariantType,
    hideToggleButtons,
    hasWriteAccess,
}) => {
    const [anchorEl, setAnchorEl] = useState<SVGElement | null>(null);
    const [editEl, setEditEl] = useState<HTMLElement | null>(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] =
        useState<boolean>(false);

    const handleMoreIconClick = (event: any) => {
        setAnchorEl(event.target);
    };

    const handleEditClick = (event: any) => {
        setEditEl(event.target);
    };

    const handleEditClose = () => {
        setEditEl(null);
        handleMenuClose();
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const editor = useSlate();
    const confirmVariantDelete = () => {
        if (currentSelection) {
            const variantNodePath = [...currentSelection.anchor.path];
            const updatedPath =
                variantNodePath.length === 3
                    ? variantNodePath.slice(0, 2)
                    : variantNodePath;
            Transforms.removeNodes(editor, { at: updatedPath });
            setIsVariationTab(ToggleButtonValues.PREVIEW);
            setSelectedElement(undefined);
        }
        setShowDeleteConfirmation(false);
    };

    const {
        editorDetails: { formulaType, isApproved },
    } = useContext(EditorContext);

    const { data: componentTypes } = useComponentTypesQuery();

    const isDataset = selectedElement?.kind === ElementKind.dataset;

    const variantType = selectedElement?.variantType;

    const showContentTag = useMemo<boolean>(
        () =>
            !!formulaId &&
            !!formulaType &&
            !!FormulaTypesData[formulaType].supportContentTag,
        [formulaId, formulaType],
    );

    const showTemplate = useMemo<boolean>(
        () => !!formulaId && formulaType === FormulaType.whatsapp,
        [formulaType, formulaId],
    );

    const componentType = useMemo(() => {
        return componentTypes?.map[variantType];
    }, [componentTypes, variantType]);

    const handleVariantDelete = () => {
        handleEditClose();
        setShowDeleteConfirmation(true);
    };

    const toggleButtons = useMemo(() => {
        const baseButtons: ToggleButtonProps[] = [
            {
                title: (
                    <>
                        {componentType?.name ? (
                            <>{componentType?.name}</>
                        ) : (
                            'Add alternates'
                        )}
                    </>
                ),
                color: componentType?.secondary_color,
                value: ToggleButtonValues.VARIATION,
                isLeft: true,
                isSelected: isVariationTab,
                onChange: setIsVariationTab,
                icon: (
                    <ComponentTypesIconWrapper
                        dataKey={
                            (componentType as ComponentTypeMapped)?.data_key
                        }
                    />
                ),
                tooltip:
                    !isDataset &&
                    'Add and manage text alternates and add personalization labels.',
            },
            {
                title: 'Preview',
                value: ToggleButtonValues.PREVIEW,
                isSelected: isVariationTab,
                onChange: setIsVariationTab,
                isLeft: isApproved,
                isRight: true,
                tooltip:
                    'See a preview of how these messages will look like on a phone.',
            },
        ];

        if (showTemplate) {
            baseButtons.push({
                title: 'Copy template',
                value: ToggleButtonValues.WHATSAPP_TEMPLATE,
                isSelected: isVariationTab,
                isRight: true,
                onChange: setIsVariationTab,
            });
        }

        if (showContentTag) {
            baseButtons.push({
                title: 'Content Tag',
                value: ToggleButtonValues.CONTENT_TAG,
                isSelected: isVariationTab,
                isRight: true,
                onChange: setIsVariationTab,
                tooltip: 'Copy your content URL',
            });
        }
        return baseButtons;
    }, [
        isDataset,
        isVariationTab,
        setIsVariationTab,
        isApproved,
        showTemplate,
        showContentTag,
        componentType,
    ]);

    const componentTypesData = useMemo(() => {
        return componentTypes?.map ?? {};
    }, [componentTypes]);

    const updateVariantTypeMutation = useComponentTypeUpdate(
        formulaId || 0,
        variantId || '',
    );

    const queryClient = useQueryClient();
    const { track } = useAnalytics();

    const handleVariantChange = async (
        variantId: string,
        newVariantType: string,
        componentId: string,
    ) => {
        const confirmed = !!(await confirm(
            'Switch Personalization type?',
            'warning',
            `This will move your alternates from ${componentTypesData[variantType].name} to ${componentTypesData[newVariantType].name}. Labels will only be kept if they already exist in ${componentTypesData[newVariantType].name}.`,
        ));
        if (confirmed) {
            updateVariantTypeMutation.mutate(
                {
                    id: variantId,
                    data: { variant_type: newVariantType },
                },
                {
                    onSuccess: () => {
                        editVariantType(variantId, newVariantType, componentId);
                        queryClient.invalidateQueries({
                            queryKey: ['labels', newVariantType],
                        });
                        queryClient.invalidateQueries({
                            queryKey:
                                alternateKeys.getVariantsByComponentId(
                                    variantId,
                                ),
                        });
                        track('component_type_changed', {
                            new_variant_type: newVariantType,
                            new_component_type_id: componentId,
                            original_variant_type: variantType,
                            formula_id: formulaId,
                            variant_id: variantId,
                        });
                    },
                },
            );
        }
    };

    return (
        <Container>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <EditMenu>
                    <EditType onClick={handleEditClick}>
                        Change Component
                        <ICONS.rightChevron size={'24px'} />
                    </EditType>
                    <EditType onClick={() => handleVariantDelete()}>
                        Delete
                    </EditType>
                    <Popover
                        open={!!editEl}
                        anchorEl={editEl}
                        onClose={handleEditClose}
                        anchorOrigin={{
                            vertical: 'center',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'center',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            style: { overflow: 'unset' },
                        }}
                    >
                        <VariantTypeDropdown
                            currentVariantType={variantType}
                            variantId={variantId || ''}
                            formulaId={formulaId || 0}
                            handleVariantChange={handleVariantChange}
                            handleMenuClose={handleEditClose}
                            showOnlyOptions
                            shouldConfirm
                        />
                    </Popover>
                </EditMenu>
            </Popover>
            {showDeleteConfirmation && (
                <ConfirmPrompt
                    message={`Are you sure you want to delete this Component?`}
                    description={`Any alternates that you've created below will be lost.`}
                    type={'warning'}
                    abortHandler={() => {
                        setShowDeleteConfirmation(false);
                    }}
                    confirmHandler={confirmVariantDelete}
                    style={{ marginBottom: aampeTheme.spacing(2) }}
                />
            )}
            {!hideToggleButtons && (
                <ButtonContainer>
                    <AampeToggleButton
                        buttons={toggleButtons}
                        areButtonsCurved={false}
                    />
                </ButtonContainer>
            )}
            <Steps>
                {isVariationTab === ToggleButtonValues.VARIATION &&
                    selectedElement && (
                        <AssistantSteps
                            setIsVariationTab={setIsVariationTab}
                            setSelectedElement={setSelectedElement}
                            currentSelection={currentSelection}
                            formulaId={formulaId}
                            selectedElement={selectedElement}
                            alternateCreateError={alternateCreateError}
                            setAlternateCreateError={setAlternateCreateError}
                            handleIconClick={handleMoreIconClick}
                            removeSpacing={hideToggleButtons}
                            hasWriteAccess={hasWriteAccess}
                        />
                    )}
                {isVariationTab === ToggleButtonValues.PREVIEW &&
                    !hideToggleButtons && <MessagePreviewMain />}
                {isVariationTab === ToggleButtonValues.WHATSAPP_TEMPLATE && (
                    <WhatsAppTemplateMain />
                )}
                {isVariationTab === ToggleButtonValues.CONTENT_TAG && (
                    <ContentTag formulaId={formulaId!} />
                )}
            </Steps>
        </Container>
    );
};

const Container = styled.div(() => ({
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: aampeTheme.spacing4(5),
    height: '100%',
}));
const ButtonContainer = styled.div({});
const Steps = styled.div(() => ({
    height: '95%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
}));
const EditType = styled.span({
    display: 'flex',
    gap: aampeTheme.spacing4(2),
    fontSize: aampeTheme.size.font.default,
    lineHeight: '20px',
    fontWeight: 400,
    color: aampeTheme.colors.solidA01,
    alignItems: 'center',
    backgroundColor: aampeTheme.colors.white,
    border: 'none',
    cursor: 'pointer',
});

const EditMenu = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
    padding: aampeTheme.spacing4(4),
});

export default SidebarMain;
